import type { Config } from '@packages/config';

/**
 * Derives the unique root path prefixes owned by the order app from the order config.
 *
 * When a `locale` is provided, the locale-specific path is preferred over `default`
 * for each entry. This is important where pathnames differ (e.g. `fr-CH` uses `/mon-compte` instead of `/mein-konto`).
 * Falls back to `default` when no locale-specific path exists for a given entry.
 *
 * @param paths  The `paths` map from `OrderConfig`.
 * @param locale Optional locale string (e.g. `'fr-CH'`, `'nl-NL'`). When omitted, `default` is used for every entry.
 *
 * @example
 * // Without locale (default paths):
 * getOrderAppRoutePrefixes(orderConfig.paths)
 * // → ['/warenkorb', '/kasse', '/mein-konto', '/merkzettel', '/paylink', '/verify', '/credit-check']
 *
 * @example
 * // With locale 'fr-CH' — entries that have a 'fr-CH' override use that path:
 * getOrderAppRoutePrefixes(orderConfig.paths, 'fr-CH')
 * // → ['/warenkorb', '/kasse', '/mon-compte', '/merkzettel', '/paylink', '/verify', '/credit-check']
 *
 */
export const getOrderAppRoutePrefixes = (
  paths: Config['order']['paths'],
  locale?: string,
): string[] => {
  const prefixes = new Set<string>();
  for (const localizedPath of Object.values(paths)) {
    const localizedValue = locale
      ? (localizedPath as Record<string, string | undefined>)[locale]
      : undefined;
    const path = localizedValue ?? localizedPath.default;
    const firstSegment = path.split('/').find(Boolean);
    if (firstSegment) prefixes.add(`/${firstSegment}`);
  }
  return [...prefixes];
};
